import { Injectable, inject } from '@angular/core';
import {
  Action,
  createSelector,
  Selector,
  State,
  StateContext,
} from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { captureException } from '@sentry/angular';
import { UserTimelinesGateway } from '@wilson/api/gateway';
import {
  ActivitiesAssignment,
  Activity,
  RoleIDsMap,
  Shift,
  ShiftAssignment,
  TimelineTimeframe,
  UserTimelinesModal,
  UserTimelinesWithActivitiesPayload,
} from '@wilson/interfaces';
import {
  GanttScrollLeftPixelService,
  ShiftOverlappingService,
} from '@wilson/shift-timeline/services';
import { ShiftAssignmentService } from '@wilson/shifts';
import {
  addDays,
  differenceInDays,
  eachDayOfInterval,
  endOfDay,
  startOfDay,
} from 'date-fns';
import { catchError, Observable, of, Subject, takeUntil, tap } from 'rxjs';
import { ShiftTimelineFilterStateService } from '../shift-timeline-filters/shift-timeline-filter-state.service';
import {
  ResetShiftAssignmentModalState,
  SetActivitiesAssignmentWindowWidth,
  SetActivitiesToAssign,
  SetShiftToAssign,
  SetWindowWidth,
  TimelineFetchActivitiesAssignment,
  TimelineFetchShiftAssignments,
  TimelineModalFetchUsers,
  TimelineModalSetDaysRange,
  TimelineModalSetTimeframeWidth,
  ToggleFilterLoading,
} from './shift-assignment-modal.actions';

export enum MODAL_VIEW {
  SHIFT = 'SHIFT',
  ACTIVITY = 'ACTIVITY',
}

const defaultTimeFrame = {
  start: startOfDay(new Date()).getTime(),
  end: endOfDay(addDays(new Date(), 6)).getTime(),
};

const timeframeWidthByModalView = new Map([
  [MODAL_VIEW.SHIFT, 480],
  [MODAL_VIEW.ACTIVITY, 1920],
]);

export interface ShiftAssignmentModalStateModel {
  timeframe: TimelineTimeframe;
  daysRangeCount: number;
  timeframeWidth: number;
  resourcesColumnWidthPx: number;
  scrollToPx: number;
  windowWidth: number;
  shiftToAssign: (Shift & { activities: Activity[] }) | null;
  activitiesToAssign: Activity[];
  showLoading: boolean;
  isLoadingUsers: boolean;
  usersDictionary: Record<string, UserTimelinesModal>;
  shiftAssignment: ShiftAssignment[] | ActivitiesAssignment[];
  hasFetchedShiftAssignmentSuccessful: boolean;
}

export const defaults: ShiftAssignmentModalStateModel = {
  timeframe: defaultTimeFrame,
  daysRangeCount: eachDayOfInterval(defaultTimeFrame).length,
  timeframeWidth: timeframeWidthByModalView.get(MODAL_VIEW.SHIFT) as number,
  resourcesColumnWidthPx: 440,
  scrollToPx: 0,
  windowWidth: 0,
  shiftToAssign: null,
  activitiesToAssign: [],
  showLoading: false,
  isLoadingUsers: false,
  usersDictionary: {},
  shiftAssignment: [],
  hasFetchedShiftAssignmentSuccessful: false,
};

@State<ShiftAssignmentModalStateModel>({
  name: 'shiftAssignmentModal',
  defaults,
})
@Injectable()
export class ShiftAssignmentModalState {
  private ganttScrollLeftPixelService = inject(GanttScrollLeftPixelService);
  private userTimelineGateway = inject(UserTimelinesGateway);
  private shiftAssignmentService = inject(ShiftAssignmentService);
  private shiftTimelineFilterStateService = inject(
    ShiftTimelineFilterStateService,
  );

  private unSubFetchUserRequestSubject = new Subject();

  @Selector()
  static resourcesColumnWidthPx(state: ShiftAssignmentModalStateModel): number {
    return state.resourcesColumnWidthPx;
  }

  @Selector()
  static timeframe(state: ShiftAssignmentModalStateModel): TimelineTimeframe {
    return state.timeframe;
  }

  @Selector()
  static frameLength(state: ShiftAssignmentModalStateModel): number {
    return state.daysRangeCount * state.timeframeWidth;
  }

  @Selector()
  static shiftToAssign(
    state: ShiftAssignmentModalStateModel,
  ): (Shift & { activities: Activity[] }) | null {
    return state.shiftToAssign;
  }

  @Selector()
  static activitiesToAssign(state: ShiftAssignmentModalStateModel): Activity[] {
    return state.activitiesToAssign;
  }

  @Selector()
  static scrollToPx(state: ShiftAssignmentModalStateModel): number {
    return state.scrollToPx;
  }

  @Selector()
  static timelineModalUsers(
    state: ShiftAssignmentModalStateModel,
  ): UserTimelinesModal[] {
    return Object.values(state.usersDictionary);
  }

  @Selector()
  static showFilterLoading(state: ShiftAssignmentModalStateModel): boolean {
    return state.showLoading;
  }

  @Selector()
  static isFetchedShiftAssignmentSuccessful(
    state: ShiftAssignmentModalStateModel,
  ): boolean {
    return state.hasFetchedShiftAssignmentSuccessful;
  }

  @Selector()
  static shiftAssignment(
    state: ShiftAssignmentModalStateModel,
  ): ShiftAssignment[] | ActivitiesAssignment[] {
    return state.shiftAssignment;
  }

  static userShiftAssignment(
    userId: string | null,
  ): (
    shiftAssignment: ShiftAssignment[] | ActivitiesAssignment[],
  ) => ShiftAssignment | ActivitiesAssignment | undefined {
    return createSelector(
      [ShiftAssignmentModalState.shiftAssignment],
      (shiftAssignment: ShiftAssignment[] | ActivitiesAssignment[]) => {
        return shiftAssignment.find(
          (timelineShiftAssignment) =>
            timelineShiftAssignment.user.id === userId,
        );
      },
    );
  }

  @Action(ResetShiftAssignmentModalState)
  resetShiftAssignmentState(
    ctx: StateContext<ShiftAssignmentModalStateModel>,
  ): void {
    ctx.patchState(defaults);
  }

  @Action(SetWindowWidth)
  setWindowWith(
    { patchState, getState }: StateContext<ShiftAssignmentModalStateModel>,
    { payload }: SetWindowWidth,
  ): void {
    const state = getState();
    const updatePayload: Pick<
      ShiftAssignmentModalStateModel,
      'scrollToPx' | 'windowWidth'
    > = {
      windowWidth: payload.width,
      scrollToPx: state.scrollToPx,
    };

    if (state.shiftToAssign) {
      updatePayload.scrollToPx = this.updateScrollToPx({
        startDatetime: state.shiftToAssign.activities[0].startDatetime,
        endDatetime:
          state.shiftToAssign.activities[
            state.shiftToAssign.activities.length - 1
          ].endDatetime,
        windowWidth: payload.width,
        frameLength: state.daysRangeCount * state.timeframeWidth,
        dayCount: state.daysRangeCount,
        timeframe: state.timeframe,
        resourcesColumnWidthPx: state.resourcesColumnWidthPx,
      });
    }

    patchState(updatePayload);
  }

  @Action(SetActivitiesAssignmentWindowWidth)
  setActivitiesAssignmentWindowWidth(
    { patchState, getState }: StateContext<ShiftAssignmentModalStateModel>,
    { payload }: SetActivitiesAssignmentWindowWidth,
  ): void {
    const state = getState();
    const updatePayload: Pick<
      ShiftAssignmentModalStateModel,
      'scrollToPx' | 'windowWidth'
    > = {
      windowWidth: payload.width,
      scrollToPx: state.scrollToPx,
    };

    const shiftAssignment = state.shiftAssignment;

    const firstShiftActivities = shiftAssignment[0]?.shift.activities;
    if (firstShiftActivities && firstShiftActivities.length) {
      updatePayload.scrollToPx = this.updateScrollToPx({
        startDatetime: firstShiftActivities[0].startDatetime,
        endDatetime:
          firstShiftActivities[firstShiftActivities.length - 1].endDatetime,
        windowWidth: payload.width,
        frameLength: state.daysRangeCount * state.timeframeWidth,
        dayCount: state.daysRangeCount,
        timeframe: state.timeframe,
        resourcesColumnWidthPx: state.resourcesColumnWidthPx,
      });
    }

    patchState(updatePayload);
  }

  @Action(SetShiftToAssign)
  setShiftToAssign(
    { patchState, getState }: StateContext<ShiftAssignmentModalStateModel>,
    { payload }: SetShiftToAssign,
  ): void {
    const state = getState();
    const updatePayload: Pick<
      ShiftAssignmentModalStateModel,
      'shiftToAssign' | 'scrollToPx'
    > = {
      shiftToAssign: payload.shift,
      scrollToPx: state.scrollToPx,
    };
    updatePayload.scrollToPx = this.updateScrollToPx({
      startDatetime: payload.shift.activities[0].startDatetime,
      endDatetime:
        payload.shift.activities[payload.shift.activities.length - 1]
          .endDatetime,
      windowWidth: state.windowWidth,
      frameLength: state.daysRangeCount * state.timeframeWidth,
      dayCount: state.daysRangeCount,
      timeframe: state.timeframe,
      resourcesColumnWidthPx: state.resourcesColumnWidthPx,
    });

    patchState(updatePayload);
  }

  @Action(ToggleFilterLoading)
  toggleFilterLoading(
    ctx: StateContext<ShiftAssignmentModalStateModel>,
    action: ToggleFilterLoading,
  ): void {
    ctx.setState(
      patch<ShiftAssignmentModalStateModel>({
        showLoading: action.isLoading,
      }),
    );
  }

  @Action(TimelineModalSetDaysRange)
  timelineModalSetDaysRange(
    ctx: StateContext<ShiftAssignmentModalStateModel>,
    action: TimelineModalSetDaysRange,
  ): void {
    ctx.setState(
      patch<ShiftAssignmentModalStateModel>({
        timeframe: {
          start: new Date(action.dates.start).getTime(),
          end: new Date(action.dates.end).getTime(),
        },
        daysRangeCount:
          differenceInDays(action.dates.end, action.dates.start) + 1,
      }),
    );
  }

  @Action(TimelineModalSetTimeframeWidth)
  TimelineModalSetTimeframeWidth(
    ctx: StateContext<ShiftAssignmentModalStateModel>,
    action: TimelineModalSetTimeframeWidth,
  ): void {
    ctx.setState(
      patch<ShiftAssignmentModalStateModel>({
        timeframeWidth: timeframeWidthByModalView.get(action.value),
      }),
    );
  }

  @Action(TimelineModalFetchUsers)
  async timelineModalFetchUsers(
    ctx: StateContext<ShiftAssignmentModalStateModel>,
    action: TimelineModalFetchUsers,
  ): Promise<
    Observable<
      | UserTimelinesWithActivitiesPayload[]
      | {
          isRequestError: boolean;
        }
    >
  > {
    this.unSubFetchUserRequestSubject.next(null);
    if (action.timeframe.start && action.timeframe.end) {
      ctx.patchState({
        isLoadingUsers: true,
      });

      const options =
        await this.shiftTimelineFilterStateService.generateFilterOptionsForAssignedRegion(
          action.timeframe.start as Date,
          action.timeframe.end as Date,
          null,
        );

      return this.userTimelineGateway
        .getUserTimelinesWithActivities(options)
        .pipe(
          takeUntil(this.unSubFetchUserRequestSubject),
          tap((users) => {
            users = users.filter((user) => {
              if (action.assignmentTimeRange) {
                return (
                  user.userRoles.findIndex(
                    (role) => role.roleId === RoleIDsMap.OperationalStaff,
                  ) !== -1 &&
                  !ShiftOverlappingService.isShiftToAssignOverlappingWithNonRelevantAbsences(
                    user,
                    {
                      start: action.assignmentTimeRange.start,
                      end: action.assignmentTimeRange.end,
                    },
                  )
                );
              } else {
                return (
                  user.userRoles.findIndex(
                    (role) => role.roleId === RoleIDsMap.OperationalStaff,
                  ) !== -1
                );
              }
            });
            ctx.patchState({
              usersDictionary: Object.fromEntries(
                users.map((user) => [
                  user.id,
                  {
                    ...user,
                    shiftAssignment: undefined,
                  },
                ]),
              ),
              isLoadingUsers: false,
            });
          }),
          catchError(() =>
            of({
              isRequestError: true,
              data: [],
            }),
          ),
        );
    } else {
      return of({
        isRequestError: true,
        data: [],
      });
    }
  }

  @Action(TimelineFetchShiftAssignments)
  timelineFetchShiftAssignments(
    ctx: StateContext<ShiftAssignmentModalStateModel>,
    action: TimelineFetchShiftAssignments,
  ): Observable<ShiftAssignment[]> {
    ctx.patchState({
      hasFetchedShiftAssignmentSuccessful: false,
    });

    return this.shiftAssignmentService
      .getShiftAssignments(action.shiftToAssignId, true)
      .pipe(
        tap((shiftAssignment) => {
          ctx.patchState({
            shiftAssignment: shiftAssignment,
            hasFetchedShiftAssignmentSuccessful: true,
          });
        }),
        catchError((error) => {
          captureException(error);
          ctx.patchState({
            shiftAssignment: [],
            hasFetchedShiftAssignmentSuccessful: false,
          });
          return of([]);
        }),
      );
  }

  @Action(TimelineFetchActivitiesAssignment)
  timelineFetchActivitiesAssignment(
    ctx: StateContext<ShiftAssignmentModalStateModel>,
    action: TimelineFetchActivitiesAssignment,
  ): Observable<ActivitiesAssignment[]> {
    ctx.patchState({
      hasFetchedShiftAssignmentSuccessful: false,
    });

    return this.shiftAssignmentService
      .getActivitiesAssignments(action.selectedActivityIds)
      .pipe(
        tap((activitiesAssignment) => {
          const state = ctx.getState();
          const filteredUsers = Object.values(state.usersDictionary).filter(
            (user) =>
              activitiesAssignment.find(
                (assignment) => assignment.user.id === user.id,
              ),
          );
          ctx.patchState({
            usersDictionary: Object.fromEntries(
              filteredUsers.map((user) => [user.id, user]),
            ),
            shiftAssignment: activitiesAssignment,
            hasFetchedShiftAssignmentSuccessful: true,
          });
        }),
      );
  }

  @Action(SetActivitiesToAssign)
  setActivitiesToAssign(
    { patchState }: StateContext<ShiftAssignmentModalStateModel>,
    { payload }: SetActivitiesToAssign,
  ): void {
    const updatePayload: Pick<
      ShiftAssignmentModalStateModel,
      'activitiesToAssign'
    > = {
      activitiesToAssign: payload,
    };
    patchState(updatePayload);
  }

  private updateScrollToPx({
    startDatetime,
    endDatetime,
    windowWidth,
    frameLength,
    dayCount,
    timeframe,
    resourcesColumnWidthPx,
  }: {
    startDatetime: string;
    endDatetime: string;
    windowWidth: number;
    frameLength: number;
    dayCount: number;
    timeframe: TimelineTimeframe;
    resourcesColumnWidthPx: number;
  }): number {
    return this.ganttScrollLeftPixelService.calculate({
      frameLength: frameLength,
      dayCount: dayCount,
      timeframe: timeframe,
      startTime: startDatetime,
      endTime: endDatetime,
      windowWidth,
      resourcesColumnWidthPx: resourcesColumnWidthPx,
    });
  }
}
